import { markRaw } from 'vue'; // Mark component definitions as raw to prevent Vue from making them reactive
import Activity from '@/models/Activity';
import Asset from '@/models/Asset';
import AssetCategory from '@/models/AssetCategory';
import AssetStatus from '@/models/AssetStatus';
import Contract from '@/models/Contract';
import Invoice from '@/models/Invoice';
import Message from '@/models/Message';
import PrepaidCard from '@/models/PrepaidCard';
import Priority from '@/models/Priority';
import Quote from '@/models/Quote';
import Subscription from '@/models/Subscription';
import TablePreset from '@/models/TablePreset';
import Ticket from '@/models/Ticket';
import TicketStatus from '@/models/TicketStatus';
import TimeRegistration from '@/models/TimeRegistration';
import UsedProduct from '@/models/UsedProduct';
import AssetShowComponent from '@/views/assets/Show.vue';
import ContractShowComponent from '@/views/contracts/Show.vue';
import QuoteShowComponent from '@/views/quotes/Show.vue';
import SubscriptionShowComponent from '@/views/subscriptions/Show.vue';

export const BACKEND_MODELS = {
    'activities': {
        model: Activity,
        label: 'models.activity',
    },
    'asset-categories': {
        model: AssetCategory,
        label: 'models.assetcategory',
    },
    'assets': {
        class: 'App\\Models\\Tenant\\Asset',
        model: Asset,
        showComponent: markRaw(AssetShowComponent),
        label: 'models.asset',
        loadWith: ['company', 'client', 'location', 'categories', 'operator'],
        loadWithModel: ['operator', 'actor', 'client', 'company', 'categories', 'location'],
        prefill: { operator_id: null },
        config: {
            type: 'assets',
            defaultTableColumns: [
                {
                    title: 'asset.name',
                    visible: true,
                    component: 'Name',
                    name: 'name',
                    to: 'assets.show',
                    orderable: 'name',
                    select: 'DefaultString',
                    filter: 'name',
                    filter_type: 'string',
                },
                {
                    title: 'models.company',
                    component: 'Company',
                    name: 'company',
                    visible: true,
                    filter: 'company_id',
                    select: true,
                    filter_type: 'select',
                },
                {
                    title: 'models.client',
                    component: 'Client',
                    name: 'client',
                    visible: true,
                    select: true,
                    filter: 'client_id',
                    filter_type: 'select',
                },
                {
                    title: 'models.location',
                    component: 'Location',
                    name: 'location',
                    visible: false,
                    select: true,
                    filter: 'location_id',
                    filter_type: 'select',
                },
                {
                    title: 'time.last-updated',
                    component: 'DateFromNow',
                    name: 'updated_at',
                    visible: true,
                    orderable: 'updated_at',
                },
                {
                    title: 'models.category',
                    component: 'TicketCategories',
                    name: 'categories',
                    visible: true,
                    select: 'AssetCategories',
                    filter: 'category_id',
                    multi_select: true,
                    filter_type: 'relation',
                },
                {
                    title: 'time.created-at',
                    component: 'DateTime',
                    name: 'created_at',
                    visible: true,
                    orderable: 'created_at',
                },
                {
                    title: 'asset.description',
                    component: 'DefaultString',
                    name: 'description',
                    visible: true,
                    orderable: 'description',
                    select: true,
                    filter: 'description',
                    filter_type: 'string',
                },
                {
                    title: 'asset.serial-number',
                    component: 'DefaultString',
                    name: 'serial_number',
                    visible: true,
                    orderable: 'serial_number',
                    select: true,
                    filter: 'serial_number',
                    filter_type: 'string',
                },
                {
                    title: 'asset.tracking-number',
                    component: 'DefaultString',
                    name: 'tracking_number',
                    visible: true,
                    orderable: 'tracking_number',
                    select: true,
                    filter: 'tracking_number',
                    filter_type: 'string',
                },
                {
                    title: 'asset.warranty-until',
                    component: 'WarrantyRemaining',
                    name: 'warranty_until',
                    visible: true,
                    orderable: 'warranty_until',
                    select: 'YesNo',
                    filter: 'warranty_until',
                    filter_type: 'boolean',
                    options: [
                        {
                            label: 'asset.filter.with-warranty',
                            id: 1,
                        },
                        {
                            label: 'asset.filter.without-warranty',
                            id: 0,
                        },
                    ],
                },
            ],
            defaultOrder: {
                by: 'name',
                direction: 'ASC',
            },
            searchColumns: [
                {
                    component: 'GlobalSearchString',
                    name: 'name',
                    to: 'assets.show',
                },
                {
                    title: 'asset.tracking-number',
                    component: 'DefaultString',
                    name: 'tracking_number',
                },
                {
                    title: 'models.company',
                    component: 'Company',
                    name: 'company',
                },
                {
                    title: 'models.client',
                    component: 'Client',
                    name: 'Client',
                },
            ],
        },
    },
    'categories': {
        label: 'model.category',
    },
    'contracts': {
        class: 'App\\Models\\Tenant\\Contract',
        model: Contract,
        showComponent: markRaw(ContractShowComponent),
        label: 'models.contract',
        loadWith: ['service', 'client', 'contractPeriods'],
        loadWithModel: [
            'contractPeriods',
            'prepaidCard',
            'client',
            'service',
            'ticketTypes',
            'operator',
            'activities',
            'contractPeriods.timeRegistrations',
            'contractPeriods.timeRegistrations.morphable',
            'contractPeriods.timeRegistrations.actor',
            'contractPeriods.timeRegistrations.activity',
        ],
        config: {
            type: 'contracts',
            defaultTableColumns: [
                {
                    title: 'contract.name',
                    visible: true,
                    component: 'Name',
                    name: 'name',
                    to: 'contracts.show',
                    orderable: 'name',
                },
                // {
                //     title: 'models.service',
                //     component: 'DefaultRelatedObject',
                //     name: 'service',
                //     visible: true,
                //     select: 'Service',
                //     filter: 'service_id',
                //     multi_select: true,
                //     filter_type: 'multi_select'
                // },
                // {
                //     title: 'models.company',
                //     component: 'Companies',
                //     name: 'companies',
                //     replaces: 'company',
                //     visible: true,
                //     select: true,
                //     multi_select: true,
                //     filter: 'morphable_id',
                //     filter_morph: 'App\\Models\\Tenant\\Company',
                //     filter_type: 'morph_to'
                // },
                {
                    title: 'models.client',
                    component: 'Client',
                    name: 'client',
                    visible: true,
                    select: true,
                    filter: 'client_id',
                    filter_type: 'select',
                },
                {
                    title: 'contract.period',
                    visible: true,
                    component: 'PeriodString',
                    name: 'period',
                    filter: 'period',
                    select: 'ContractPeriod',
                    filter_type: 'select',
                },
                {
                    title: 'contract.remaining-period',
                    visible: true,
                    component: 'MinutesRemaining',
                    name: 'minutes_remaining',
                    orderable: 'minutes_remaining',
                },
                {
                    title: 'time.last-updated',
                    component: 'DateFromNow',
                    name: 'updated_at',
                    orderable: 'updated_at',
                    visible: false,
                },
                {
                    title: 'contract.hours-per-period',
                    visible: false,
                    component: 'DefaultInt',
                    name: 'quantity',
                    orderable: 'quantity',
                },
                {
                    title: 'time.created-at',
                    visible: false,
                    component: 'DateTime',
                    name: 'created_at',
                    orderable: 'created_at',
                },
            ],
            defaultOrder: {
                by: 'name',
                direction: 'ASC',
            },
        },
    },
    'custom-fields': {
        label: 'models.customfield',
    },
    'invoices': {
        model: Invoice,
        label: 'models.invoice',
        noAutoReload: true,
        config: {
            defaultTableColumns: [
                {
                    title: 'models.invoice',
                    component: 'DefaultString',
                    name: 'InvoiceCode',
                    visible: true,
                },
                {
                    title: 'invoice.reference',
                    component: 'DefaultString',
                    name: 'ReferenceNumber',
                    visible: true,
                },
                {
                    title: 'invoice.amount',
                    component: 'Currency',
                    name: 'AmountIncl',
                    visible: true,
                },
                {
                    title: 'invoice.date',
                    component: 'DefaultString',
                    name: 'Date',
                    visible: true,
                },
                {
                    title: 'models.company',
                    component: 'DefaultString',
                    name: 'CompanyName',
                    visible: true,
                },
                {
                    title: 'defaults.status',
                    component: 'InvoiceStatus',
                    name: 'Status',
                    visible: true,
                },
                {
                    title: 'actions.download',
                    component: 'InvoiceDownload',
                    name: 'ViewURL',
                    visible: true,
                },
            ],
            defaultOrder: {
                by: 'name',
                direction: 'ASC',
            },
        },
    },
    'messages': {
        model: Message,
        label: 'models.message',
        loadWith: ['morphable'],
        config: {
            searchColumns: [
                {
                    component: 'HtmlString',
                    name: 'message',
                    to: 'tickets.show',
                },
                {
                    title: 'message.type',
                    component: 'DefaultMorphable',
                    name: 'morphable_type',
                },
            ],
        },
    },
    'prepaid-cards': {
        model: PrepaidCard,
        label: 'models.prepaidcard',
    },
    'priorities': {
        model: Priority,
        label: 'models.priority',
    },
    'quotes': {
        class: 'App\\Models\\Tenant\\Quote',
        model: Quote,
        showComponent: markRaw(QuoteShowComponent),
        label: 'models.quote',
        loadWith: ['deal', 'deal.client', 'deal.company'],
        loadWithModel: ['deal', 'deal.company', 'deal.client'],
        appendWithModel: ['custom_fields'],
        config: {
            defaultTableColumns: [
                {
                    title: 'quote.name',
                    component: 'Quote',
                    name: 'name',
                    visible: true,
                    select: 'DefaultString',
                    filter: 'name',
                    filter_type: 'string',
                },
                {
                    title: 'quote.status',
                    component: 'QuoteStatus',
                    name: 'status_id',
                    visible: true,
                    orderable: 'status_id',
                    select: 'QuoteStatus',
                    filter: 'status_id',
                    filter_type: 'multi_select',
                    width: '150px',
                },
                {
                    title: 'models.client',
                    component: 'Client',
                    name: 'deal.client',
                    visible: true,
                    select: 'Client',
                    filter: 'deal.client_id',
                    multi_select: true,
                    filter_relation: 'deal.client',
                    filter_type: 'relation',
                },
                {
                    title: 'models.company',
                    component: 'Company',
                    name: 'deal.company',
                    visible: true,
                    select: 'Company',
                    filter: 'deal.company_id',
                    filter_relation: 'deal.company',
                    filter_type: 'relation',
                    multi_select: true,
                },
                {
                    title: 'time.last-updated',
                    component: 'DateFromNow',
                    name: 'updated_at',
                    visible: true,
                    orderable: 'updated_at',
                },
                {
                    title: 'time.created-at',
                    component: 'DateTime',
                    name: 'created_at',
                    visible: false,
                    orderable: 'created_at',
                },
            ],
            defaultOrder: {
                by: 'name',
                direction: 'ASC',
            },
        },
    },
    'services': {
        label: 'models.service',
    },
    'ticket-statuses': {
        model: TicketStatus,
        label: 'models.ticketstatus',
    },
    'asset-statuses': {
        model: AssetStatus,
        label: 'models.assetstatus',
    },
    'subscriptions': {
        class: 'App\\Models\\Tenant\\Subscription',
        model: Subscription,
        showComponent: markRaw(SubscriptionShowComponent),
        label: 'models.subscription',
        loadWith: ['company', 'customValues'],
        loadWithModel: [
            'company',
            'lines',
            'customValues',
            'customValues.customFieldObject',
            'customValues.customFieldObject.options',
        ],
        appendWithModel: ['custom_fields'],
        config: {
            type: 'subscriptions',
            defaultTableColumns: [
                {
                    title: 'subscription.description',
                    component: 'Name',
                    name: 'description',
                    visible: true,
                    orderable: 'description',
                    to: 'subscriptions.show',
                    select: 'DefaultString',
                    filter: 'description',
                    filter_type: 'string',
                },
                {
                    title: 'models.company',
                    component: 'Company',
                    name: 'company',
                    visible: true,
                    filter: 'company_id',
                    select: true,
                    filter_type: 'select',
                    orderable: 'companies.name',
                    orderable_relation: 'company',
                },
                {
                    title: 'subscription.total',
                    component: 'Currency',
                    name: 'total',
                    visible: true,
                    orderable: 'total',
                },
                {
                    title: 'subscription.monthly',
                    component: 'Currency',
                    name: 'mrr',
                    visible: true,
                    orderable: 'mrr',
                },
                {
                    title: 'subscription.period',
                    visible: true,
                    component: 'PeriodString',
                    name: 'period',
                    filter: 'period',
                    select: 'SubscriptionPeriod',
                    filter_type: 'select',
                },
                {
                    title: 'subscription.next-invoice',
                    component: 'Date',
                    name: 'next_billing_date',
                    visible: true,
                    orderable: 'next_billing_date',
                },
                {
                    title: 'subscription.direct-debit',
                    component: 'YesNo',
                    name: 'direct_debit',
                    select: 'YesNo',
                    visible: true,
                    filter: 'direct_debit',
                    filter_type: 'select',
                    orderable: 'direct_debit',
                },
                {
                    title: 'subscription.termination-date',
                    component: 'Date',
                    name: 'termination_date',
                    visible: true,
                    orderable: 'termination_date',
                },
                {
                    title: 'time.last-updated',
                    component: 'DateFromNow',
                    name: 'updated_at',
                    visible: true,
                    orderable: 'updated_at',
                },
                {
                    title: 'time.created-at',
                    component: 'DateTime',
                    name: 'created_at',
                    visible: true,
                    orderable: 'created_at',
                },
            ],
            searchColumns: [
                {
                    title: 'subscription.description',
                    component: 'Name',
                    name: 'description',
                },
                {
                    title: 'models.company',
                    component: 'Company',
                    name: 'company',
                },
                {
                    title: 'subscription.total',
                    component: 'Currency',
                    name: 'total',
                },
            ],
            defaultOrder: {
                by: 'next_billing_date',
                direction: 'ASC',
            },
        },
    },
    'table-presets': {
        model: TablePreset,
        label: 'models.tablepreset',
    },
    'tickets': {
        class: 'App\\Models\\Tenant\\Ticket',
        model: Ticket,
        label: 'models.ticket',
        loadWith: [
            'client',
            'status',
            'company',
        ],
        config: {
            defaultTableColumns: [
                {
                    title: 'ticket.reference',
                    component: 'TicketReference',
                    name: 'reference',
                    visible: true,
                    select: 'DefaultString',
                    filter: 'reference',
                    filter_type: 'string',
                    orderable: 'reference',
                    width: '120px',
                },
                {
                    title: 'ticket.status',
                    component: 'TicketStatus',
                    name: 'status',
                    visible: true,
                    orderable: 'status_id',
                    select: 'TicketStatus',
                    filter: 'status.name_client',
                    filter_type: 'relation',
                    width: '150px',
                },
                {
                    title: 'ticket.description',
                    component: 'TicketDescription',
                    name: 'description',
                    visible: true,
                    orderable: 'description',
                    filter: 'description',
                    filter_type: 'string',
                    select: 'DefaultString',
                },
                {
                    title: 'models.company',
                    component: 'Company',
                    name: 'company',
                    visible: true,
                    select: true,
                    filter: 'company_id',
                    filter_type: 'select',
                    orderable: 'companies.name',
                    orderable_relation: 'company',
                },
                {
                    title: 'models.client',
                    component: 'Client',
                    name: 'client',
                    visible: true,
                    select: true,
                    filter: 'client_id',
                    filter_type: 'select',
                },
                {
                    title: 'time.last-updated',
                    component: 'DateFromNow',
                    name: 'updated_at',
                    visible: true,
                    orderable: 'updated_at',
                },
                {
                    title: 'time.created-at',
                    component: 'DateTime',
                    name: 'created_at',
                    visible: false,
                    orderable: 'created_at',
                },
                {
                    title: 'time.closed-at',
                    component: 'DateTime',
                    name: 'resolved_at',
                    select: 'DateRange',
                    filter: 'resolved_at',
                    filter_type: 'date_between',
                    visible: false,
                    orderable: 'resolved_at',
                },
            ],
            defaultOrder: {
                by: 'updated_at',
                direction: 'DESC',
            },
            searchColumns: [
                {
                    title: 'ticket.reference',
                    component: 'TicketReference',
                    name: 'reference',
                    to: 'tickets.show',
                },
                {
                    title: 'ticket.description',
                    component: 'GlobalSearchString',
                    name: 'description',
                    to: 'tickets.show',
                },
                {
                    title: 'models.company',
                    component: 'Company',
                    name: 'company',
                },
            ],
        },
    },
    'ticket-sources': {
        label: 'models.ticketsource',
    },
    'ticket-types': {
        class: 'App\\Models\\Tenant\\TicketType',
    },
    'time-registrations': {
        model: TimeRegistration,
        label: 'models.timeregistration',
    },
    'used-products': {
        model: UsedProduct,
        label: 'models.usedproduct',
    },
};

export const WIDGET_TYPES = {
    table: 'table',
};

export const LANGUAGES = {
    nl: 'Nederlands',
    en: 'English',
    de: 'Deutsch',
};
